import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { updateProfile } from 'firebase/auth'
import { doc, updateDoc } from 'firebase/firestore'
import { storage, auth, db } from '../../lib/firebase'
import { logger } from '../../utils/logger'

export const uploadProfilePhoto = async (file: File): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  logger.debug('🔐 Uploading profile photo')

  // Validate file
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image')
  }

  if (file.size > 5 * 1024 * 1024) { // 5MB limit
    throw new Error('File size must be less than 5MB')
  }

  console.log('📁 File info:', {
    name: file.name,
    type: file.type,
    size: file.size
  })

  try {
    // Create storage reference to match your Firebase rules
    const fileExtension = file.name.split('.').pop()
    const fileName = `users/${auth.currentUser.uid}/profile/avatar.${fileExtension}`
    
    logger.debug('📂 Upload path:', fileName)
    
    const storageRef = ref(storage, fileName)

    // Upload file
    const snapshot = await uploadBytes(storageRef, file)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    // Update Firebase Auth profile photoURL
    await updateProfile(auth.currentUser, {
      photoURL: downloadURL
    })

    // Also update Firestore user document
    const userRef = doc(db, 'users', auth.currentUser.uid)
    await updateDoc(userRef, {
      profilePhoto: downloadURL,
      updatedAt: new Date()
    })

    return downloadURL
  } catch (error) {
    console.error('Error uploading profile photo:', error)
    throw new Error('Failed to upload photo. Please try again.')
  }
}

export const uploadThumbnail = async (blob: Blob, draftId: string): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  try {
    // Create storage reference for thumbnail
    const fileName = `thumbnails/${auth.currentUser.uid}/${draftId}.jpg`
    const storageRef = ref(storage, fileName)

    console.log('📸 Uploading thumbnail to:', fileName)

    // Upload blob
    const snapshot = await uploadBytes(storageRef, blob)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    console.log('✅ Thumbnail uploaded successfully:', downloadURL)
    return downloadURL
  } catch (error) {
    console.error('❌ Error uploading thumbnail:', error)
    throw new Error('Failed to upload thumbnail')
  }
}

export const deleteThumbnail = async (draftId: string): Promise<void> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  try {
    const fileName = `thumbnails/${auth.currentUser.uid}/${draftId}.jpg`
    const storageRef = ref(storage, fileName)
    await deleteObject(storageRef)
    console.log('🗑️ Thumbnail deleted successfully:', fileName)
  } catch (error) {
    console.warn('⚠️ Could not delete thumbnail (may not exist):', error)
  }
}

export const uploadRoutePhoto = async (file: File, photoId: string): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  // Validate file
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image')
  }

  if (file.size > 100 * 1024 * 1024) { // 100MB limit for route photos
    throw new Error('File size must be less than 100MB')
  }

  try {
    // Create storage reference for route photo
    const fileExtension = file.name.split('.').pop() || 'jpg'
    const fileName = `photos/${auth.currentUser.uid}/${photoId}.${fileExtension}`
    
    console.log('📸 Uploading route photo to:', fileName)
    
    const storageRef = ref(storage, fileName)

    // Upload file
    const snapshot = await uploadBytes(storageRef, file)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    console.log('✅ Route photo uploaded successfully:', downloadURL)
    return downloadURL
  } catch (error) {
    console.error('❌ Error uploading route photo:', error)
    throw new Error('Failed to upload photo. Please try again.')
  }
}

export const uploadPhotoThumbnail = async (thumbnailBlob: Blob, photoId: string): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  try {
    // Create storage reference for photo thumbnail
    const fileName = `photos/${auth.currentUser.uid}/${photoId}_thumb.jpg`
    const storageRef = ref(storage, fileName)

    console.log('📸 Uploading photo thumbnail to:', fileName)

    // Upload blob
    const snapshot = await uploadBytes(storageRef, thumbnailBlob)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    console.log('✅ Photo thumbnail uploaded successfully:', downloadURL)
    return downloadURL
  } catch (error) {
    console.error('❌ Error uploading photo thumbnail:', error)
    throw new Error('Failed to upload thumbnail')
  }
}

export const deleteRoutePhoto = async (photoId: string): Promise<void> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  let mainPhotoDeleted = false
  let thumbnailDeleted = false

  try {
    // Delete main photo (try common extensions)
    const extensions = ['jpg', 'jpeg', 'png', 'webp', 'heic']
    
    for (const ext of extensions) {
      try {
        const photoRef = ref(storage, `photos/${auth.currentUser.uid}/${photoId}.${ext}`)
        await deleteObject(photoRef)
        console.log(`🗑️ Deleted photo: ${photoId}.${ext}`)
        mainPhotoDeleted = true
        break
      } catch (error: any) {
        // Continue trying other extensions unless it's not a 404
        if (error?.code !== 'storage/object-not-found') {
          console.error(`❌ Error deleting ${photoId}.${ext}:`, error)
        }
      }
    }

    // Delete thumbnail
    try {
      const thumbRef = ref(storage, `photos/${auth.currentUser.uid}/${photoId}_thumb.jpg`)
      await deleteObject(thumbRef)
      console.log(`🗑️ Deleted thumbnail: ${photoId}_thumb.jpg`)
      thumbnailDeleted = true
    } catch (error: any) {
      if (error?.code === 'storage/object-not-found') {
        console.warn('⚠️ Thumbnail not found (already deleted?):', `${photoId}_thumb.jpg`)
      } else {
        console.error('❌ Error deleting thumbnail:', error)
      }
    }

    // Log summary
    if (!mainPhotoDeleted && !thumbnailDeleted) {
      console.warn('⚠️ No files found for photo:', photoId, '(may have been already deleted)')
    }
  } catch (error) {
    console.error('❌ Unexpected error in deleteRoutePhoto:', error)
    throw error
  }
}

export const deleteProfilePhoto = async (): Promise<void> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  try {
    // Delete from storage if exists
    if (auth.currentUser.photoURL && auth.currentUser.photoURL.includes('firebase')) {
      // Try to determine file extension from the photoURL or use a default
      const urlParts = auth.currentUser.photoURL.split('.')
      const extension = urlParts[urlParts.length - 1].split('?')[0] // Remove query params
      const storageRef = ref(storage, `users/${auth.currentUser.uid}/profile/avatar.${extension}`)
      await deleteObject(storageRef)
    }

    // Remove from Firebase Auth profile
    await updateProfile(auth.currentUser, {
      photoURL: null
    })

    // Also remove from Firestore user document
    const userRef = doc(db, 'users', auth.currentUser.uid)
    await updateDoc(userRef, {
      profilePhoto: null,
      updatedAt: new Date()
    })
  } catch (error) {
    console.error('Error deleting profile photo:', error)
    throw new Error('Failed to delete photo. Please try again.')
  }
}

export const uploadUserPOIPhoto = async (
  userId: string,
  poiId: string,
  file: File
): Promise<string> => {
  if (!userId || !poiId) {
    throw new Error('User ID and POI ID are required')
  }

  // Validate file
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image')
  }

  // Limit file size to 10MB
  const maxSize = 10 * 1024 * 1024
  if (file.size > maxSize) {
    throw new Error('Image must be less than 10MB')
  }

  try {
    // Create a unique filename
    const timestamp = Date.now()
    const ext = file.name.split('.').pop() || 'jpg'
    const filename = `${timestamp}.${ext}`
    const filePath = `user_pois/${userId}/${poiId}/${filename}`

    console.log('📸 Uploading user POI photo to:', filePath)
    console.log('📁 File details:', {
      name: file.name,
      type: file.type,
      size: file.size,
      sizeInMB: (file.size / (1024 * 1024)).toFixed(2) + 'MB'
    })
    console.log('🔐 Auth state:', {
      currentUser: auth.currentUser?.uid,
      uploadingForUser: userId,
      match: auth.currentUser?.uid === userId
    })

    const fileRef = ref(storage, filePath)
    const snapshot = await uploadBytes(fileRef, file)
    const downloadURL = await getDownloadURL(snapshot.ref)

    console.log('✅ User POI photo uploaded successfully:', downloadURL)
    return downloadURL
  } catch (error) {
    console.error('❌ Error uploading user POI photo:', error)
    throw new Error('Failed to upload photo. Please try again.')
  }
}

export const deleteUserPOIPhoto = async (photoUrl: string): Promise<void> => {
  try {
    const url = new URL(photoUrl)
    const pathMatch = url.pathname.match(/\/o\/(.+?)\?/)
    if (!pathMatch) return

    const filePath = decodeURIComponent(pathMatch[1])
    const fileRef = ref(storage, filePath)
    await deleteObject(fileRef)
    console.log('🗑️ Deleted user POI photo:', filePath)
  } catch (error: any) {
    if (error?.code === 'storage/object-not-found') {
      console.warn('⚠️ Photo not found (already deleted?):', photoUrl)
    } else {
      console.error('❌ Error deleting user POI photo:', error)
      throw error
    }
  }
}

export const uploadClubLogo = async (file: File, clubId?: string): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  // Validate file
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image')
  }

  if (file.size > 5 * 1024 * 1024) { // 5MB limit
    throw new Error('File size must be less than 5MB')
  }

  console.log('🏢 Uploading club logo:', {
    name: file.name,
    type: file.type,
    size: file.size,
    clubId: clubId || 'new-club'
  })

  try {
    // Create storage reference for club logo
    // Use photos/ path which we know works, with club-logo prefix
    const fileExtension = file.name.split('.').pop()
    const timestamp = Date.now()
    const logoId = clubId ? `club-logo-${clubId}-${timestamp}` : `club-logo-${timestamp}`
    const fileName = `photos/${auth.currentUser.uid}/${logoId}.${fileExtension}`
    
    console.log('📂 Club logo upload path:', fileName)
    
    const storageRef = ref(storage, fileName)

    // Upload file
    const snapshot = await uploadBytes(storageRef, file)
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    console.log('✅ Club logo uploaded successfully:', downloadURL)
    return downloadURL
  } catch (error) {
    console.error('❌ Error uploading club logo:', error)
    throw new Error('Failed to upload club logo. Please try again.')
  }
}

export const deleteRouteLogo = async (logoUrl: string): Promise<void> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  try {
    // Extract the storage path from the URL
    const url = new URL(logoUrl)
    const pathMatch = url.pathname.match(/\/o\/(.+?)\?/)
    if (!pathMatch) {
      throw new Error('Invalid logo URL')
    }

    const filePath = decodeURIComponent(pathMatch[1])
    console.log('🗑️ Deleting route logo from:', filePath)
    
    const storageRef = ref(storage, filePath)
    await deleteObject(storageRef)
    
    console.log('✅ Route logo deleted successfully')
  } catch (error: any) {
    if (error?.code === 'storage/object-not-found') {
      console.warn('⚠️ Logo not found (already deleted?):', logoUrl)
    } else {
      console.error('❌ Error deleting route logo:', error)
      throw new Error('Failed to delete logo. Please try again.')
    }
  }
}

/**
 * Upload JSON data to Firebase Storage for mobile app consumption
 * This is used to store uncompressed route data that iOS can download directly
 */
export const uploadRouteJSON = async (path: string, data: any): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('No authenticated user')
  }

  try {
    // Convert data to JSON string
    const jsonString = JSON.stringify(data)
    const blob = new Blob([jsonString], { type: 'application/json' })
    
    // Create storage reference
    const storageRef = ref(storage, path)
    
    console.log('📤 Uploading JSON to:', path)
    console.log('📊 Data size:', (blob.size / 1024).toFixed(2), 'KB')
    
    // Upload blob
    const snapshot = await uploadBytes(storageRef, blob, {
      contentType: 'application/json',
      cacheControl: 'public, max-age=3600' // Cache for 1 hour
    })
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    
    console.log('✅ JSON uploaded successfully:', downloadURL)
    return downloadURL
  } catch (error) {
    console.error('❌ Error uploading JSON:', error)
    throw new Error('Failed to upload route data')
  }
}

/**
 * Upload route segment data to Cloud Storage when it needs compression
 * Returns URLs for each uploaded file
 */
export const uploadRouteSegmentData = async (
  routeId: string, 
  segmentIndex: number, 
  segmentData: any
): Promise<Record<string, string>> => {
  const urls: Record<string, string> = {}
  const userId = auth.currentUser?.uid
  
  if (!userId) {
    throw new Error('No authenticated user')
  }

  // Only upload fields that are being compressed
  const uploads: Promise<void>[] = []
  
  // Check if coordinates are compressed
  if (segmentData._compressed?.coordinates && segmentData.coordinates) {
    uploads.push(
      uploadRouteJSON(
        `routes/${userId}/${routeId}/segments/${segmentIndex}/coordinates.json`,
        segmentData.coordinates // Original uncompressed data
      ).then(url => { urls.coordinatesUrl = url })
    )
  }
  
  // Check if elevation profile is compressed
  // Note: originalData.elevation contains just the profile data, not the full elevation object
  if (segmentData._compressed?.elevationProfile && segmentData.elevation) {
    uploads.push(
      uploadRouteJSON(
        `routes/${userId}/${routeId}/segments/${segmentIndex}/elevation.json`,
        segmentData.elevation // This is already the profile array
      ).then(url => { urls.elevationUrl = url })
    )
  }
  
  // Check if surfaces are compressed
  if (segmentData._compressed?.surfaces && segmentData.surfaces) {
    uploads.push(
      uploadRouteJSON(
        `routes/${userId}/${routeId}/segments/${segmentIndex}/surfaces.json`,
        segmentData.surfaces
      ).then(url => { urls.surfacesUrl = url })
    )
  }
  
  // Check if unpaved segments are compressed
  if (segmentData._compressed?.unpavedSegments && segmentData.unpavedSegments) {
    uploads.push(
      uploadRouteJSON(
        `routes/${userId}/${routeId}/segments/${segmentIndex}/unpaved.json`,
        segmentData.unpavedSegments
      ).then(url => { urls.unpavedUrl = url })
    )
  }
  
  // Wait for all uploads to complete
  if (uploads.length > 0) {
    console.log(`📤 Uploading ${uploads.length} data files for segment ${segmentIndex}`)
    
    // Use allSettled to handle partial failures
    const results = await Promise.allSettled(uploads)
    
    // Log any failures
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`❌ Failed to upload file ${index}: ${result.reason}`)
      }
    })
    
    // Return whatever URLs we successfully uploaded
    console.log(`✅ Successfully uploaded ${Object.keys(urls).length}/${uploads.length} files`)
  }
  
  return urls
}